import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "components/PageLayout";
import Skeleton from "components/Skeleton";
import ConfirmDialog from "components/ConfirmDialog";
import EmptyState from "components/EmptyState";
import { useRerunnerConfigs } from "hooks/useRerunnerConfigs";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { useToast } from "components/Toast";
import { Plus, Pencil, Trash2, Settings } from "lucide-react";

export default function RerunnerList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const companyId = useEffectiveCompanyId();
  const { toast } = useToast();
  const { configs, loading, error, refetch } = useRerunnerConfigs();
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", description: "", onConfirm: () => {} });

  function handleDelete(e, config) {
    e.stopPropagation();
    setConfirmDialog({
      open: true,
      title: "Delete Config",
      description: `Delete config "${config.clientName}"? This cannot be undone.`,
      confirmLabel: "Delete",
      variant: "destructive",
      onConfirm: async () => {
        setConfirmDialog((c) => ({ ...c, open: false }));
        try {
          await makeApiRequest(
            "delete",
            apiRoutes.deleteRerunnerConfig(config.clientId),
            { id: user.id, accessToken: user.accessToken },
            companyId
          );
          toast({ status: "success", title: "Config deleted", description: `${config.clientName} has been removed.` });
          refetch();
        } catch (err) {
          toast({ status: "destructive", title: "Delete failed", description: "Something went wrong. Please try again." });
        }
      },
    });
  }

  if (error) return <p className="text-destructive p-6">Error loading configs: {error.message}</p>;

  return (
    <div>
      <PageHeader
        title="Rerunner Configs"
        subtitle="Manage automated decline recovery configurations"
        actions={
          <button className="btn btn-primary" onClick={() => navigate("/rerunner/create")}>
            <Plus className="h-4 w-4" />
            Create Config
          </button>
        }
      />

      <div className="container py-6">
        <div className="surface rounded-xl ring-1 ring-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Client Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Enabled</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Schedule</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Last Updated</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>

              <tbody className="bg-card divide-y divide-border">
                {loading &&
                  [0, 1, 2].map((i) => (
                    <tr key={i}>
                      {[0, 1, 2, 3, 4, 5].map((j) => (
                        <td key={j} className="px-6 py-4">
                          <Skeleton.Rect className="h-4 w-24" />
                        </td>
                      ))}
                    </tr>
                  ))}

                {!loading &&
                  configs.map((c) => (
                    <tr
                      key={c.clientId}
                      onClick={() => navigate(`/rerunner/${c.clientId}/edit`)}
                      className="hover:bg-muted transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4 text-sm font-medium">{c.clientName}</td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">{c.uniqueCompanyName}</td>
                      <td className="px-6 py-4">
                        <span className={`badge ${c.enabled ? "badge-brand" : "badge-muted"}`}>
                          {c.enabled ? "Active" : "Disabled"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground font-mono">
                        {c.schedule || "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">
                        {c.updatedAt ? new Date(c.updatedAt).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            className="btn btn-ghost h-8 w-8 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/rerunner/${c.clientId}/edit`);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            className="btn btn-ghost h-8 w-8 p-0 text-destructive"
                            onClick={(e) => handleDelete(e, c)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                {!loading && configs.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <EmptyState
                        icon={Settings}
                        heading="No rerunner configs found."
                        action={
                          <button
                            className="btn btn-primary"
                            onClick={() => navigate("/rerunner/create")}
                          >
                            <Plus className="h-4 w-4" />
                            Create First Config
                          </button>
                        }
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ConfirmDialog
        {...confirmDialog}
        onCancel={() => setConfirmDialog((c) => ({ ...c, open: false }))}
      />
    </div>
  );
}
